import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { IntegrationType } from "@types";
import { captureSentryException } from "@utils";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSnackbar } from "src/components/Snackbar/useSnackbar";
import { getMcpConnectionsQueryKey } from "src/config";
import {
  MCP_CONNECTION_PRESET_KEYS,
  MCP_PRESET_CONFIGS,
  type McpConnectionPresetConfig,
  type McpConnectionPresetKey,
} from "src/config/mcp-presets";
import {
  useCreateAuthlessMcpConnectionMutation,
  useCreateManualMcpConnectionMutation,
  useDisconnectIntegration,
  useDisconnectMcpConnectionMutation,
  useSendSlackTestMutation,
  useSetIntegrationEnabledMutation,
  useSetMcpConnectionEnabledMutation,
  useStartMcpOAuthConnectionMutation,
  useTestMcpConnectionMutation,
} from "src/hooks/mutations";
import { useGithubOAuthFlow } from "src/hooks/useGithubIntegration";
import { useJiraOAuthFlow } from "src/hooks/useJiraIntegration";
import { useLinearOAuthFlow } from "src/hooks/useLinearIntegration";
import { useSlackOAuthFlow } from "src/hooks/useSlackIntegration";
import { getMcpFaviconUrls } from "src/utils/mcp-favicon";
import {
  getAllIntegrations,
  getIntegrationConfig,
} from "../../config/integrations";
import {
  useGetIntegration,
  useGetMcpConnections,
  useGetSlackIntegration,
} from "../../hooks/queries";
import { AddMcpServerModal } from "./AddMcpServerModal";
import {
  IntegrationCard,
  IntegrationCardConfig,
  McpConnectionCard,
} from "./IntegrationCard";
import {
  getMcpAddConnectionErrorMessage,
  INVALID_MCP_SERVER_URL_MESSAGE,
  isValidHttpsMcpServerUrl,
} from "./utils/mcp-errors";

interface IntegrationsPanelProps {
  isAddMcpModalOpen?: boolean;
  onCloseAddMcpModal?: () => void;
}

const isMcpPresetKey = (
  presetKey: string | undefined
): presetKey is McpConnectionPresetKey =>
  Boolean(
    presetKey &&
      (MCP_CONNECTION_PRESET_KEYS as readonly string[]).includes(presetKey)
  );

export const IntegrationsPanel: React.FC<IntegrationsPanelProps> = ({
  isAddMcpModalOpen = false,
  onCloseAddMcpModal,
}) => {
  const { orgId = "" } = useParams<{ orgId: string }>();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [integrationToDisconnect, setIntegrationToDisconnect] =
    useState<IntegrationType | null>(null);
  const [mcpConnectionToDisconnect, setMcpConnectionToDisconnect] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [addMcpName, setAddMcpName] = useState("");
  const [addMcpServerUrl, setAddMcpServerUrl] = useState("");
  const [addMcpAccessToken, setAddMcpAccessToken] = useState("");
  const [addMcpError, setAddMcpError] = useState<string | null>(null);
  const [testingConnectionId, setTestingConnectionId] = useState<string | null>(
    null
  );
  const [connectingMcpPresetKey, setConnectingMcpPresetKey] = useState<
    string | null
  >(null);
  const [togglingIntegrationId, setTogglingIntegrationId] =
    useState<IntegrationType | null>(null);
  const [togglingMcpConnectionId, setTogglingMcpConnectionId] = useState<
    string | null
  >(null);

  const integrations = getAllIntegrations();
  const disconnectMutation = useDisconnectIntegration();
  const testMutation = useSendSlackTestMutation(orgId);
  const createManualMcpMutation = useCreateManualMcpConnectionMutation();
  const createAuthlessMcpMutation = useCreateAuthlessMcpConnectionMutation();
  const startMcpOAuthMutation = useStartMcpOAuthConnectionMutation();
  const disconnectMcpMutation = useDisconnectMcpConnectionMutation();
  const testMcpMutation = useTestMcpConnectionMutation();
  const setIntegrationEnabledMutation = useSetIntegrationEnabledMutation();
  const setMcpConnectionEnabledMutation = useSetMcpConnectionEnabledMutation();

  const { startOAuthFlow: startSlackOAuth, isLoading: isSlackOAuthLoading } =
    useSlackOAuthFlow(orgId);
  const { startOAuthFlow: startLinearOAuth, isLoading: isLinearOAuthLoading } =
    useLinearOAuthFlow(orgId);
  const { startOAuthFlow: startGithubOAuth, isLoading: isGithubOAuthLoading } =
    useGithubOAuthFlow(orgId);
  const { startOAuthFlow: startJiraOAuth, isLoading: isJiraOAuthLoading } =
    useJiraOAuthFlow(orgId);

  const { data: slackData, isLoading: isSlackLoading } =
    useGetSlackIntegration(orgId);
  const { data: linearData, isLoading: isLinearLoading } = useGetIntegration(
    orgId,
    IntegrationType.LINEAR
  );
  const { data: githubData, isLoading: isGithubLoading } = useGetIntegration(
    orgId,
    IntegrationType.GITHUB
  );
  const { data: jiraData, isLoading: isJiraLoading } = useGetIntegration(
    orgId,
    IntegrationType.JIRA
  );
  const { data: mcpConnectionsData, isLoading: isMcpConnectionsLoading } =
    useGetMcpConnections(orgId, !!orgId);

  useEffect(() => {
    if (!isAddMcpModalOpen) {
      setAddMcpName("");
      setAddMcpServerUrl("");
      setAddMcpAccessToken("");
      setAddMcpError(null);
    }
  }, [isAddMcpModalOpen]);

  const integrationData = {
    [IntegrationType.SLACK]: {
      data: slackData?.integration,
      isLoading: isSlackLoading,
    },
    [IntegrationType.GITHUB]: {
      data: githubData?.integration,
      isLoading: isGithubLoading,
    },
    [IntegrationType.LINEAR]: {
      data: linearData?.integration,
      isLoading: isLinearLoading,
    },
    [IntegrationType.JIRA]: {
      data: jiraData?.integration,
      isLoading: isJiraLoading,
    },
  };

  const mcpConnections = mcpConnectionsData?.connections || [];
  const mcpPresetConnectionsByKey = MCP_CONNECTION_PRESET_KEYS.reduce(
    (acc, presetKey) => {
      acc[presetKey] =
        mcpConnections.find(
          (connection) => connection.presetKey === presetKey
        ) || null;
      return acc;
    },
    {} as Record<McpConnectionPresetKey, (typeof mcpConnections)[number] | null>
  );
  const customMcpConnections = mcpConnections.filter(
    (connection) => !isMcpPresetKey(connection.presetKey)
  );

  const openMcpOAuthPopup = (authUrl: string) => {
    const popup = window.open(
      authUrl,
      "mcp-oauth",
      "width=650,height=760,scrollbars=yes,resizable=yes"
    );

    if (!popup) {
      throw new Error("OAuth popup was blocked by the browser");
    }

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      if (event.data?.type === "mcp_OAUTH_SUCCESS") {
        queryClient.invalidateQueries({
          queryKey: getMcpConnectionsQueryKey(orgId),
        });
        queryClient.refetchQueries({
          queryKey: getMcpConnectionsQueryKey(orgId),
        });
        window.removeEventListener("message", handleMessage);
      }

      if (event.data?.type === "mcp_OAUTH_ERROR") {
        showSnackbar(event.data?.message || "MCP OAuth flow failed.");
        window.removeEventListener("message", handleMessage);
      }
    };

    window.addEventListener("message", handleMessage);

    const checkClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkClosed);
        window.removeEventListener("message", handleMessage);
        queryClient.invalidateQueries({
          queryKey: getMcpConnectionsQueryKey(orgId),
        });
      }
    }, 1000);
  };

  const handleConnect = async (integrationId: IntegrationType) => {
    switch (integrationId) {
      case IntegrationType.SLACK:
        await startSlackOAuth();
        break;
      case IntegrationType.GITHUB:
        await startGithubOAuth();
        break;
      case IntegrationType.LINEAR:
        await startLinearOAuth();
        break;
      case IntegrationType.JIRA:
        await startJiraOAuth();
        break;
      default:
        captureSentryException(
          new Error(`Unknown integration type: ${integrationId}`),
          { integrationId }
        );
    }
  };

  const handleDisconnect = (integrationId: IntegrationType) => {
    setIntegrationToDisconnect(integrationId);
    setMcpConnectionToDisconnect(null);
    setDisconnectDialogOpen(true);
  };

  const handleDisconnectMcp = (
    connectionId: string,
    connectionName: string
  ) => {
    setMcpConnectionToDisconnect({ id: connectionId, name: connectionName });
    setIntegrationToDisconnect(null);
    setDisconnectDialogOpen(true);
  };

  const handleConnectMcpPreset = async (preset: McpConnectionPresetConfig) => {
    setConnectingMcpPresetKey(preset.presetKey);
    try {
      if (preset.authMode === "oauth") {
        const data = await startMcpOAuthMutation.mutateAsync({
          orgId,
          name: preset.card.name,
          serverUrl: preset.serverUrl,
          presetKey: preset.presetKey,
        });
        openMcpOAuthPopup(data.authUrl);
      } else {
        await createAuthlessMcpMutation.mutateAsync({
          orgId,
          name: preset.card.name,
          serverUrl: preset.serverUrl,
          presetKey: preset.presetKey,
        });
      }
    } catch (error) {
      showSnackbar(preset.failureMessage);
      captureSentryException(error, {
        orgId,
        presetKey: preset.presetKey,
        serverUrl: preset.serverUrl,
      });
    } finally {
      setConnectingMcpPresetKey(null);
    }
  };

  const confirmDisconnect = async () => {
    if (integrationToDisconnect) {
      try {
        await disconnectMutation.mutateAsync({
          orgId,
          type: integrationToDisconnect,
        });
      } catch (error) {
        console.error("Failed to disconnect integration:", error);
      }
    }

    if (mcpConnectionToDisconnect) {
      try {
        await disconnectMcpMutation.mutateAsync({
          orgId,
          connectionId: mcpConnectionToDisconnect.id,
        });
      } catch (error) {
        console.error("Failed to disconnect MCP connection:", error);
        showSnackbar("Failed to disconnect MCP connection.");
      }
    }

    setDisconnectDialogOpen(false);
    setIntegrationToDisconnect(null);
    setMcpConnectionToDisconnect(null);
  };

  const handleTest = async (integrationId: IntegrationType) => {
    switch (integrationId) {
      case IntegrationType.SLACK:
        try {
          await testMutation.mutateAsync({ message: undefined });
        } catch (error) {
          captureSentryException(error, { integrationId });
        }
        break;
      case IntegrationType.GITHUB:
        break;
      case IntegrationType.LINEAR:
        break;
      case IntegrationType.JIRA:
        break;
      default:
        captureSentryException(
          new Error(`Unknown integration type: ${integrationId}`),
          { integrationId }
        );
    }
  };

  const handleCloseAddMcpModal = () => {
    onCloseAddMcpModal?.();
  };

  const handleSubmitAddMcpConnection = async () => {
    const name = addMcpName.trim();
    const serverUrl = addMcpServerUrl.trim();
    const accessToken = addMcpAccessToken.trim();

    if (!name || !serverUrl) {
      setAddMcpError("Connection name and MCP server URL are required.");
      return;
    }

    if (!isValidHttpsMcpServerUrl(serverUrl)) {
      setAddMcpError(INVALID_MCP_SERVER_URL_MESSAGE);
      return;
    }

    setAddMcpError(null);

    if (accessToken) {
      try {
        await createManualMcpMutation.mutateAsync({
          orgId,
          name,
          serverUrl,
          accessToken,
        });
        handleCloseAddMcpModal();
      } catch (error) {
        setAddMcpError(
          getMcpAddConnectionErrorMessage(
            error,
            "Failed to create manual MCP connection."
          )
        );
        captureSentryException(error, {
          orgId,
          serverUrl,
          mode: "manual_bearer",
        });
      }
      return;
    }

    try {
      const data = await startMcpOAuthMutation.mutateAsync({
        orgId,
        name,
        serverUrl,
      });
      openMcpOAuthPopup(data.authUrl);
      handleCloseAddMcpModal();
    } catch (error) {
      setAddMcpError(
        getMcpAddConnectionErrorMessage(
          error,
          "Failed to start MCP OAuth flow."
        )
      );
      captureSentryException(error, {
        orgId,
        serverUrl,
        mode: "oauth",
      });
    }
  };

  const handleTestMcpConnection = async (connectionId: string) => {
    setTestingConnectionId(connectionId);
    try {
      await testMcpMutation.mutateAsync({
        orgId,
        connectionId,
      });
    } catch (error) {
      showSnackbar("Failed to test MCP connection.");
      captureSentryException(error, {
        orgId,
        connectionId,
      });
    } finally {
      setTestingConnectionId(null);
    }
  };

  const handleToggleIntegrationEnabled = async (
    integrationId: IntegrationType,
    enabled: boolean
  ) => {
    setTogglingIntegrationId(integrationId);
    try {
      await setIntegrationEnabledMutation.mutateAsync({
        orgId,
        type: integrationId,
        enabled,
      });
    } catch (error) {
      showSnackbar("Failed to update integration access.");
      captureSentryException(error, {
        orgId,
        integrationId,
        enabled,
      });
    } finally {
      setTogglingIntegrationId(null);
    }
  };

  const handleToggleMcpEnabled = async (
    connectionId: string,
    enabled: boolean
  ) => {
    setTogglingMcpConnectionId(connectionId);
    try {
      await setMcpConnectionEnabledMutation.mutateAsync({
        orgId,
        connectionId,
        enabled,
      });
    } catch (error) {
      showSnackbar("Failed to update MCP integration access.");
      captureSentryException(error, {
        orgId,
        connectionId,
        enabled,
      });
    } finally {
      setTogglingMcpConnectionId(null);
    }
  };

  const getIntegrationStatus = (
    integrationId: IntegrationType
  ): "connected" | "disconnected" | "coming_soon" => {
    if (integrationId === IntegrationType.SLACK) {
      if (isSlackLoading) return "disconnected";
      return slackData?.integration?.metadata.currentUserBinding.connected
        ? "connected"
        : "disconnected";
    }

    const integrationInfo = integrationData[integrationId];

    if (integrationInfo) {
      if (integrationInfo.isLoading) return "disconnected";
      return integrationInfo.data ? "connected" : "disconnected";
    }

    const config = getIntegrationConfig(integrationId);
    return config?.status || "disconnected";
  };

  const getIntegrationLoadingState = (integrationId: string) => {
    return {
      isConnecting:
        (integrationId === IntegrationType.SLACK && isSlackOAuthLoading) ||
        (integrationId === IntegrationType.LINEAR && isLinearOAuthLoading) ||
        (integrationId === IntegrationType.GITHUB && isGithubOAuthLoading) ||
        (integrationId === IntegrationType.JIRA && isJiraOAuthLoading),
      isDisconnecting:
        disconnectMutation.isPending &&
        integrationToDisconnect === integrationId,
      isTesting:
        testMutation.isPending && integrationId === IntegrationType.SLACK,
    };
  };

  const disconnectTargetName = integrationToDisconnect
    ? getIntegrationConfig(integrationToDisconnect)?.name
    : mcpConnectionToDisconnect?.name;
  const isDisconnectPending =
    disconnectMutation.isPending || disconnectMcpMutation.isPending;

  return (
    <Box>
      <Grid container spacing={2}>
        {integrations.map((integration) => {
          const status = getIntegrationStatus(integration.id);
          const loadingState = getIntegrationLoadingState(integration.id);
          const config = {
            ...integration,
            status,
          };

          return (
            <Grid item xs={12} md={6} lg={4} key={integration.id}>
              <IntegrationCard
                config={config}
                agentEnabled={
                  config.agentAccessMode === "agent_tools"
                    ? Boolean(integrationData[integration.id]?.data?.enabled)
                    : undefined
                }
                onToggleAgentEnabled={
                  config.agentAccessMode === "agent_tools"
                    ? (enabled) =>
                        handleToggleIntegrationEnabled(integration.id, enabled)
                    : undefined
                }
                isTogglingAgentEnabled={
                  config.agentAccessMode === "agent_tools" &&
                  togglingIntegrationId === integration.id
                }
                onConnect={() => handleConnect(integration.id)}
                onDisconnect={() => handleDisconnect(integration.id)}
                onTest={
                  integration.id === IntegrationType.SLACK
                    ? () => handleTest(integration.id)
                    : undefined
                }
                isConnecting={loadingState.isConnecting}
                isDisconnecting={loadingState.isDisconnecting}
                isTesting={loadingState.isTesting}
              />
            </Grid>
          );
        })}

        {MCP_PRESET_CONFIGS.map((preset) => {
          const connection = mcpPresetConnectionsByKey[preset.presetKey];
          const config: IntegrationCardConfig = {
            ...preset.card,
            status: connection?.connected ? "connected" : "disconnected",
          };

          return (
            <Grid item xs={12} md={6} lg={4} key={preset.presetKey}>
              <IntegrationCard
                config={config}
                agentEnabled={Boolean(connection?.enabled)}
                onToggleAgentEnabled={
                  connection
                    ? (enabled) =>
                        handleToggleMcpEnabled(connection.id, enabled)
                    : undefined
                }
                isTogglingAgentEnabled={
                  connection ? togglingMcpConnectionId === connection.id : false
                }
                onConnect={() => handleConnectMcpPreset(preset)}
                onDisconnect={
                  connection
                    ? () => handleDisconnectMcp(connection.id, preset.card.name)
                    : undefined
                }
                onTest={
                  connection
                    ? () => handleTestMcpConnection(connection.id)
                    : undefined
                }
                isConnecting={connectingMcpPresetKey === preset.presetKey}
                isDisconnecting={
                  disconnectMcpMutation.isPending &&
                  mcpConnectionToDisconnect?.id === connection?.id
                }
                isTesting={testingConnectionId === connection?.id}
              />
            </Grid>
          );
        })}

        {isMcpConnectionsLoading ? (
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">
              Loading MCP connections...
            </Typography>
          </Grid>
        ) : (
          customMcpConnections.map((connection) => (
            <Grid item xs={12} md={6} lg={4} key={connection.id}>
              <McpConnectionCard
                connection={connection}
                faviconUrl={getMcpFaviconUrls(connection.serverUrl)}
                onToggleAgentEnabled={(enabled) =>
                  handleToggleMcpEnabled(connection.id, enabled)
                }
                isTogglingAgentEnabled={
                  togglingMcpConnectionId === connection.id
                }
                onTest={() => handleTestMcpConnection(connection.id)}
                onDisconnect={() =>
                  handleDisconnectMcp(connection.id, connection.name)
                }
                isTesting={testingConnectionId === connection.id}
                isDisconnecting={
                  disconnectMcpMutation.isPending &&
                  mcpConnectionToDisconnect?.id === connection.id
                }
              />
            </Grid>
          ))
        )}
      </Grid>

      <AddMcpServerModal
        open={isAddMcpModalOpen}
        onClose={handleCloseAddMcpModal}
        onSubmit={handleSubmitAddMcpConnection}
        name={addMcpName}
        serverUrl={addMcpServerUrl}
        accessToken={addMcpAccessToken}
        onNameChange={(value) => {
          setAddMcpName(value);
          setAddMcpError(null);
        }}
        onServerUrlChange={(value) => {
          setAddMcpServerUrl(value);
          setAddMcpError(null);
        }}
        onAccessTokenChange={(value) => {
          setAddMcpAccessToken(value);
          setAddMcpError(null);
        }}
        isSubmitting={
          createManualMcpMutation.isPending || startMcpOAuthMutation.isPending
        }
        errorMessage={addMcpError}
      />

      <Dialog
        open={disconnectDialogOpen}
        onClose={() => setDisconnectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Disconnect Integration</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to disconnect {disconnectTargetName || "this"}
            ? The assistant will no longer be able to interact with it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            color="inherit"
            onClick={() => setDisconnectDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDisconnect}
            color="error"
            variant="contained"
            disabled={isDisconnectPending}
          >
            {isDisconnectPending ? "Disconnecting..." : "Disconnect"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
